/**
 * ============================================================================
 * SINGLE SOURCE OF TRUTH - ESG Questions Mapping
 * ============================================================================
 * 
 * Questo file centralizza TUTTE le domande ESG con mapping completo tra:
 * - ID Backend (chiave di salvataggio, NON modificabile)
 * - Numerazione visuale UI (E1.1, S2.3, G1.1, ecc.)
 * - Testo della domanda
 * - Sezione e ordinamento
 * 
 * IMPORTANTE:
 * - Gli ID backend sono DEFINITIVI (es. q06_ghg_scope_1_2)
 * - Il backend NON è modificabile
 * - Questa mappa è la fonte definitiva per frontend e admin
 * 
 * ============================================================================
 */

/**
 * Definizione completa delle domande ESG
 * Ogni domanda contiene:
 * - id: ID backend (chiave di salvataggio nel backend)
 * - section: Sezione ESG (COMPANY, E, S, G)
 * - visualNumber: Numero visuale mostrato nella UI (es. "E1.1")
 * - order: Ordine di visualizzazione (per sort stabile)
 * - title: Testo della domanda
 * - inputType: Tipo di input ("select", "number", "text")
 * - options: Array di opzioni (solo se inputType === "select")
 */
export const ESG_QUESTIONS = [
  // ========================================================================
  // SEZIONE E: ENVIRONMENT (40 points)
  // ========================================================================
  
  // E1 - Emissions & Energy (30%)
  {
    id: "q06_ghg_scope_1_2",
    section: "E",
    subsection: "E1",
    subsectionTitle: "Emissions & Energy",
    visualNumber: "E1.1",
    order: 1,
    title: "Does the company measure at least annually its Scope 1 and 2 emissions?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q07_scope_3",
    section: "E",
    subsection: "E1",
    subsectionTitle: "Emissions & Energy",
    visualNumber: "E1.2",
    order: 2,
    title: "Does the company structurally measure part or all of Scope 3 emissions?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q08_reduction_targets",
    section: "E",
    subsection: "E1",
    subsectionTitle: "Emissions & Energy",
    visualNumber: "E1.3",
    order: 3,
    title: "Are there clear reduction targets (e.g., % CO₂ reduction by year X)?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  {
    id: "q09_renewable_energy",
    section: "E",
    subsection: "E1",
    subsectionTitle: "Emissions & Energy",
    visualNumber: "E1.4",
    order: 4,
    title: "What percentage of energy comes from renewable sources?",
    inputType: "number",
    showPercentage: true
  },
  
  // E2 - Resources & Waste (25%)
  {
    id: "q10_waste_policy",
    section: "E",
    subsection: "E2",
    subsectionTitle: "Resources & Waste",
    visualNumber: "E2.1",
    order: 5,
    title: "Is there a formalized waste management policy?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  {
    id: "q11_recycling_rate",
    section: "E",
    subsection: "E2",
    subsectionTitle: "Resources & Waste",
    visualNumber: "E2.2",
    order: 6,
    title: "Does the company measure and improve its recycling/recovery rate?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q12_packaging_reduction",
    section: "E",
    subsection: "E2",
    subsectionTitle: "Resources & Waste",
    visualNumber: "E2.3",
    order: 7,
    title: "Are there concrete initiatives to reduce packaging or critical materials?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // E3 - Supply Chain & Environmental Impact (20%)
  {
    id: "q13_supplier_criteria",
    section: "E",
    subsection: "E3",
    subsectionTitle: "Supply Chain & Environmental Impact",
    visualNumber: "E3.1",
    order: 8,
    title: "Are there minimum ESG criteria for selecting suppliers?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  {
    id: "q14_supplier_monitoring",
    section: "E",
    subsection: "E3",
    subsectionTitle: "Supply Chain & Environmental Impact",
    visualNumber: "E3.2",
    order: 9,
    title: "Are high-impact suppliers periodically monitored on environmental issues?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // E4 - Water & Pollution (15%)
  {
    id: "q15_water_measurement",
    section: "E",
    subsection: "E4",
    subsectionTitle: "Water & Pollution",
    visualNumber: "E4.1",
    order: 10,
    title: "Does the company measure and analyze water consumption?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q16_wastewater",
    section: "E",
    subsection: "E4",
    subsectionTitle: "Water & Pollution",
    visualNumber: "E4.2",
    order: 11,
    title: "Are there procedures to manage wastewater/hydrocarbons/hazardous substances?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // E5 - Green Innovation (10%)
  {
    id: "q17_green_products",
    section: "E",
    subsection: "E5",
    subsectionTitle: "Green Innovation",
    visualNumber: "E5.1",
    order: 12,
    title: "Does the company offer products/services with clear environmental benefits?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q18_certifications",
    section: "E",
    subsection: "E5",
    subsectionTitle: "Green Innovation",
    visualNumber: "E5.2",
    order: 13,
    title: "Does the company have environmental certifications?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  
  // ========================================================================
  // SEZIONE S: SOCIAL (35 points)
  // ========================================================================
  
  // S1 - Workers & Working Conditions (30%)
  {
    id: "q19_regular_contracts",
    section: "S",
    subsection: "S1",
    subsectionTitle: "Workers & Working Conditions",
    visualNumber: "S1.1",
    order: 14,
    title: "Do almost all workers have regular contracts compliant with the law?",
    inputType: "select",
    options: ["Yes", "Partial", "No"]
  },
  {
    id: "q20_health_safety",
    section: "S",
    subsection: "S1",
    subsectionTitle: "Workers & Working Conditions",
    visualNumber: "S1.2",
    order: 15,
    title: "Is there a structured health and safety system (RSPP, DVR, training, PPE)?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  {
    id: "q21_training",
    section: "S",
    subsection: "S1",
    subsectionTitle: "Workers & Working Conditions",
    visualNumber: "S1.3",
    order: 16,
    title: "Does the company plan regular training (technical + soft + ESG)?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  
  // S2 - Equity, Inclusion & Diversity (20%)
  {
    id: "q22_anti_discrimination",
    section: "S",
    subsection: "S2",
    subsectionTitle: "Equity, Inclusion & Diversity",
    visualNumber: "S2.1",
    order: 17,
    title: "Is there an explicit anti-discrimination and harassment policy?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q23_diversity",
    section: "S",
    subsection: "S2",
    subsectionTitle: "Equity, Inclusion & Diversity",
    visualNumber: "S2.2",
    order: 18,
    title: "Does the company monitor and promote diversity in key roles?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // S3 - Human Rights & Supply Chain (20%)
  {
    id: "q24_child_labor",
    section: "S",
    subsection: "S3",
    subsectionTitle: "Human Rights & Supply Chain",
    visualNumber: "S3.1",
    order: 19,
    title: "Is there a written policy on child labor, forced labor, and human rights?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q25_supply_chain_rights",
    section: "S",
    subsection: "S3",
    subsectionTitle: "Human Rights & Supply Chain",
    visualNumber: "S3.2",
    order: 20,
    title: "Does the company conduct checks on suppliers in high-risk areas?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // S4 - Community & Stakeholders (15%)
  {
    id: "q26_community_relations",
    section: "S",
    subsection: "S4",
    subsectionTitle: "Community & Stakeholders",
    visualNumber: "S4.1",
    order: 21,
    title: "Does the company have active community initiatives?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q27_stakeholder_engagement",
    section: "S",
    subsection: "S4",
    subsectionTitle: "Community & Stakeholders",
    visualNumber: "S4.2",
    order: 22,
    title: "Are there structured listening channels (surveys, forums, committees)?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  
  // S5 - Privacy, Data & Security (15%)
  {
    id: "q28_gdpr",
    section: "S",
    subsection: "S5",
    subsectionTitle: "Privacy, Data & Security",
    visualNumber: "S5.1",
    order: 23,
    title: "Are there GDPR policies and procedures with clear roles?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  {
    id: "q29_cybersecurity",
    section: "S",
    subsection: "S5",
    subsectionTitle: "Privacy, Data & Security",
    visualNumber: "S5.2",
    order: 24,
    title: "Does the company have minimum cybersecurity procedures?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // ========================================================================
  // SEZIONE G: GOVERNANCE (25 points)
  // ========================================================================
  
  // G1 - Ethics & Transparency (30%)
  {
    id: "q30_ethics_code",
    section: "G",
    subsection: "G1",
    subsectionTitle: "Ethics & Transparency",
    visualNumber: "G1.1",
    order: 25,
    title: "Is there an ethics code approved and communicated to employees/suppliers?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  {
    id: "q31_anti_corruption",
    section: "G",
    subsection: "G1",
    subsectionTitle: "Ethics & Transparency",
    visualNumber: "G1.2",
    order: 26,
    title: "Are there anti-corruption/AML procedures?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  {
    id: "q32_whistleblowing",
    section: "G",
    subsection: "G1",
    subsectionTitle: "Ethics & Transparency",
    visualNumber: "G1.3",
    order: 27,
    title: "Does the company provide a whistleblowing channel?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // G2 - Management Structure & Board (25%)
  {
    id: "q33_ownership_structure",
    section: "G",
    subsection: "G2",
    subsectionTitle: "Management Structure & Board",
    visualNumber: "G2.1",
    order: 28,
    title: "Is the shareholder/partner structure clear and documented?",
    inputType: "select",
    options: ["Yes", "Partial", "No"]
  },
  {
    id: "q34_governance",
    section: "G",
    subsection: "G2",
    subsectionTitle: "Management Structure & Board",
    visualNumber: "G2.2",
    order: 29,
    title: "Is there a governance body that meets with formal minutes?",
    inputType: "select",
    options: ["Yes", "No"]
  },
  {
    id: "q35_board_diversity",
    section: "G",
    subsection: "G2",
    subsectionTitle: "Management Structure & Board",
    visualNumber: "G2.3",
    order: 30,
    title: "Are there independent members/diversity on the board?",
    inputType: "select",
    options: ["Yes", "Partial", "No", "Not Applicable"]
  },
  
  // G3 - Compliance & Regulation (20%)
  {
    id: "q36_regulatory_mapping",
    section: "G",
    subsection: "G3",
    subsectionTitle: "Compliance & Regulation",
    visualNumber: "G3.1",
    order: 31,
    title: "Has the company identified main applicable regulations?",
    inputType: "select",
    options: ["Yes", "Partial", "No"]
  },
  {
    id: "q37_regulatory_updates",
    section: "G",
    subsection: "G3",
    subsectionTitle: "Compliance & Regulation",
    visualNumber: "G3.2",
    order: 32,
    title: "Is there a process to stay updated on regulations?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // G4 - Reporting, Financial Statements & Audits (15%)
  {
    id: "q38_financial_statements",
    section: "G",
    subsection: "G4",
    subsectionTitle: "Reporting, Financial Statements & Audits",
    visualNumber: "G4.1",
    order: 33,
    title: "Are financial statements clear, filed, and if applicable, audited?",
    inputType: "select",
    options: ["Yes", "Partial", "No"]
  },
  {
    id: "q39_esg_reporting",
    section: "G",
    subsection: "G4",
    subsectionTitle: "Reporting, Financial Statements & Audits",
    visualNumber: "G4.2",
    order: 34,
    title: "Does the company produce non-financial/ESG reports?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  },
  
  // G5 - Risks & Cybersecurity (10%)
  {
    id: "q40_risk_mapping",
    section: "G",
    subsection: "G5",
    subsectionTitle: "Risks & Cybersecurity",
    visualNumber: "G5.1",
    order: 35,
    title: "Does the company have an assessment of main risks?",
    inputType: "select",
    options: ["Yes", "Partial", "No"]
  },
  {
    id: "q41_incident_response",
    section: "G",
    subsection: "G5",
    subsectionTitle: "Risks & Cybersecurity",
    visualNumber: "G5.2",
    order: 36,
    title: "Is there a plan for critical incident response?",
    inputType: "select",
    options: ["Yes", "In Progress", "No"]
  }
];

/**
 * ============================================================================
 * UTILITY FUNCTIONS
 * ============================================================================
 */

/**
 * Mappa ID backend -> metadata domanda
 * @type {Map<string, Object>}
 */
export const QUESTIONS_BY_ID = new Map(
  ESG_QUESTIONS.map(q => [q.id, q])
);

/**
 * Ottiene i metadati di una domanda dato il suo ID backend
 * @param {string} backendId - ID backend (es. "q06_ghg_scope_1_2")
 * @returns {Object|null} - Metadati della domanda o null se non trovata
 */
export function getQuestionById(backendId) {
  return QUESTIONS_BY_ID.get(backendId) || null;
}

/**
 * Ottiene tutte le domande ordinate per section e order
 * @returns {Array} - Array di domande ordinate
 */
export function getAllQuestionsOrdered() {
  return [...ESG_QUESTIONS].sort((a, b) => a.order - b.order);
}

/**
 * Ottiene le domande raggruppate per sezione
 * @returns {Object} - Oggetto con chiavi E, S, G contenenti array di domande
 */
export function getQuestionsBySection() {
  const sections = {
    E: [],
    S: [],
    G: []
  };
  
  ESG_QUESTIONS.forEach(q => {
    if (sections[q.section]) {
      sections[q.section].push(q);
    }
  });
  
  return sections;
}

/**
 * Ottiene le domande raggruppate per subsection (E1, E2, S1, ecc.)
 * @returns {Object} - Oggetto con chiavi subsection contenenti array di domande
 */
export function getQuestionsBySubsection() {
  const subsections = {};
  
  ESG_QUESTIONS.forEach(q => {
    if (!subsections[q.subsection]) {
      subsections[q.subsection] = {
        title: q.subsectionTitle,
        section: q.section,
        questions: []
      };
    }
    subsections[q.subsection].questions.push(q);
  });
  
  return subsections;
}

/**
 * Converte una risposta dal backend (mappa ID -> value) in un array ordinato
 * per visualizzazione con metadati
 * @param {Object|Array} backendAnswers - Risposte dal backend
 * @returns {Array} - Array di risposte arricchite con metadati
 */
export function enrichAnswersWithMetadata(backendAnswers) {
  const enriched = [];
  
  // Supporta sia formato oggetto che array
  if (Array.isArray(backendAnswers)) {
    // Formato array: [{questionKey: "q06_...", answer: "Yes", description: "..."}]
    backendAnswers.forEach(item => {
      const question = getQuestionById(item.questionKey);
      if (question) {
        enriched.push({
          ...item,
          visualNumber: question.visualNumber,
          title: question.title,
          section: question.section,
          subsection: question.subsection,
          order: question.order
        });
      } else {
        // Domanda non trovata nella mappa - mostra comunque
        enriched.push({
          ...item,
          visualNumber: `UNKNOWN`,
          title: `Unknown question: ${item.questionKey}`,
          section: 'UNKNOWN',
          subsection: 'UNKNOWN',
          order: 9999
        });
      }
    });
  } else if (backendAnswers && typeof backendAnswers === 'object') {
    // Formato oggetto legacy: {q06_ghg_scope_1_2: "Yes", q07_scope_3: "No"}
    Object.entries(backendAnswers).forEach(([questionKey, answer]) => {
      const question = getQuestionById(questionKey);
      if (question) {
        enriched.push({
          questionKey,
          answer,
          visualNumber: question.visualNumber,
          title: question.title,
          section: question.section,
          subsection: question.subsection,
          order: question.order
        });
      } else {
        enriched.push({
          questionKey,
          answer,
          visualNumber: `UNKNOWN`,
          title: `Unknown question: ${questionKey}`,
          section: 'UNKNOWN',
          subsection: 'UNKNOWN',
          order: 9999
        });
      }
    });
  }
  
  // Ordina per section, poi per order
  return enriched.sort((a, b) => {
    if (a.section !== b.section) {
      const sectionOrder = { E: 1, S: 2, G: 3, UNKNOWN: 4 };
      return (sectionOrder[a.section] || 999) - (sectionOrder[b.section] || 999);
    }
    return a.order - b.order;
  });
}

/**
 * Crea un oggetto di mapping per retrocompatibilità con il vecchio formato
 * Genera chiavi fieldKey (es. e1_1_ghgScope12) -> ID backend
 * @returns {Object} - Oggetto con mapping fieldKey -> backendId
 */
export function getLegacyQuestionKeys() {
  // Mapping esplicito e stabile per CompanyVerification.jsx
  // (evita mismatch quando gli ID contengono numeri/underscore particolari)
  return {
    // E
    e1_1_ghgScope12: "q06_ghg_scope_1_2",
    e1_2_scope3: "q07_scope_3",
    e1_3_reductionTargets: "q08_reduction_targets",
    e1_4_renewableEnergy: "q09_renewable_energy",
    e2_1_wastePolicy: "q10_waste_policy",
    e2_2_recyclingRate: "q11_recycling_rate",
    e2_3_packagingReduction: "q12_packaging_reduction",
    e3_1_supplierCriteria: "q13_supplier_criteria",
    e3_2_supplierMonitoring: "q14_supplier_monitoring",
    e4_1_waterMeasurement: "q15_water_measurement",
    e4_2_wastewater: "q16_wastewater",
    e5_1_greenProducts: "q17_green_products",
    e5_2_certifications: "q18_certifications",

    // S
    s1_1_regularContracts: "q19_regular_contracts",
    s1_2_healthSafety: "q20_health_safety",
    s1_3_training: "q21_training",
    s2_1_antiDiscrimination: "q22_anti_discrimination",
    s2_2_diversity: "q23_diversity",
    s3_1_childLaborPolicy: "q24_child_labor",
    s3_2_supplyChainRights: "q25_supply_chain_rights",
    s4_1_communityRelations: "q26_community_relations",
    s4_2_stakeholderEngagement: "q27_stakeholder_engagement",
    s5_1_gdpr: "q28_gdpr",
    s5_2_cybersecurity: "q29_cybersecurity",

    // G
    g1_1_ethicsCode: "q30_ethics_code",
    g1_2_antiCorruption: "q31_anti_corruption",
    g1_3_whistleblowing: "q32_whistleblowing",
    g2_1_ownershipStructure: "q33_ownership_structure",
    g2_2_governance: "q34_governance",
    g2_3_boardDiversity: "q35_board_diversity",
    g3_1_regulatoryMapping: "q36_regulatory_mapping",
    g3_2_regulatoryUpdates: "q37_regulatory_updates",
    g4_1_financialStatements: "q38_financial_statements",
    g4_2_esgReporting: "q39_esg_reporting",
    g5_1_riskMapping: "q40_risk_mapping",
    g5_2_incidentResponse: "q41_incident_response",
  };
}
